import { takeEvery, fork, put, all, call } from "redux-saga/effects";

// Action Types
import { LOGIN_USER, LOGOUT_USER, VERIFY_AUTH_ON_LOAD } from "./actionTypes";

// Actions
import { loginSuccess, logoutUserSuccess, apiError } from "./actions";

// Helpers
import { getFirebaseBackend } from "../../../helpers/firebase_helper";
import {
  postFakeLogin,
  postJwtLogin,
} from "../../../helpers/fakebackend_helper";
import { getUser } from "services/user.service";

const fireBaseBackend = getFirebaseBackend();

/* =========================================================
   LOGIN USER
========================================================= */
function* loginUser({ payload: { user, history } }) {
  try {
    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const response = yield call(
        fireBaseBackend.loginUser,
        user.email,
        user.password
      );
      yield put(loginSuccess(response));
    } else if (process.env.REACT_APP_DEFAULTAUTH === "jwt") {
      /* ================= JWT LOGIN ================= */
      const response = yield call(
        postJwtLogin,
        process.env.REACT_APP_DATABASEURL + "/api/v1/auth/login",
        { email: user.email, password: user.password }
      );

      if (response?.success) {
        const userData = response?._doc;

        // ❌ DENY ACCESS
        if (
          userData?.role !== "Admin" ||
          userData?.adminRole !== "super" ||
          userData?.status === "deactivated"
        ) {
          alert("You do not have permission to access this portal.");
          localStorage.removeItem("authUser");
          history.push("/login");
          return;
        }

        // ✅ ALLOW ADMIN ONLY
        localStorage.setItem("authUser", JSON.stringify(response));
        yield put(loginSuccess(response));
        history.push("/dashboard");
      }
    } else if (process.env.REACT_APP_DEFAULTAUTH === "fake") {
      /* ================= FAKE LOGIN ================= */
      const response = yield call(postFakeLogin, "/post-fake-login", {
        email: user.email,
        password: user.password,
      });

      localStorage.setItem("authUser", JSON.stringify(response));
      yield put(loginSuccess(response));
    }
  } catch (error) {
    yield put(apiError(error));
  }
}

/* =========================================================
   LOGOUT USER
========================================================= */
function* logoutUser({ payload: { history } }) {
  try {
    localStorage.removeItem("authUser");

    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const response = yield call(fireBaseBackend.logout);
      yield put(logoutUserSuccess(response));
    }

    history.push("/login");
  } catch (error) {
    yield put(apiError(error));
  }
}

/* =========================================================
   VERIFY AUTH ON APP LOAD (FRONTEND ONLY)
========================================================= */
function* verifyAuthOnLoad() {
  try {
    const storedUser = localStorage.getItem("authUser");

    if (!storedUser) {
      yield put(logoutUserSuccess());
      return;
    }

    const parsedUser = JSON.parse(storedUser);
    const userId = parsedUser?._doc?._id;

    if (!userId) {
      throw new Error("Invalid user data");
    }

    // 🔥 CALL YOUR GET USER API
    const freshUserResponse = yield call(getUser, userId);

    // adjust based on your API response structure
    const freshUser =
      freshUserResponse?.data || freshUserResponse?.user || freshUserResponse;

    // ❌ ACCESS DENIED
    if (
      freshUser?.role !== "Admin" ||
      freshUser?.adminRole !== "super" ||
      freshUser?.status === "deactivated"
    ) {
      localStorage.removeItem("authUser");
      yield put(logoutUserSuccess());
      window.location.href = "/login";
      return;
    }

    // ✅ UPDATE LOCAL STORAGE WITH LATEST USER DATA
    const updatedAuthUser = {
      ...parsedUser,
      _doc: freshUser,
    };

    localStorage.setItem("authUser", JSON.stringify(updatedAuthUser));
  } catch (error) {
    console.error("verifyAuthOnLoad error:", error);
    localStorage.removeItem("authUser");
    yield put(logoutUserSuccess());
    window.location.href = "/login";
  }
}


/* =========================================================
   WATCHERS
========================================================= */
export function* watchUserLogin() {
  yield takeEvery(LOGIN_USER, loginUser);
}

export function* watchUserLogout() {
  yield takeEvery(LOGOUT_USER, logoutUser);
}

export function* watchVerifyAuthOnLoad() {
  yield takeEvery(VERIFY_AUTH_ON_LOAD, verifyAuthOnLoad);
}

/* =========================================================
   ROOT AUTH SAGA
========================================================= */
function* authSaga() {
  yield all([
    fork(watchUserLogin),
    fork(watchUserLogout),
    fork(watchVerifyAuthOnLoad), // 🔥 KEY FIX
  ]);
}

export default authSaga;
